import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ChatContext = createContext(null);

function playAlertSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (start, freq) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.32);
      o.start(ctx.currentTime + start);
      o.stop(ctx.currentTime + start + 0.34);
    };
    beep(0, 880);
    beep(0.4, 1046);
  } catch { /* noop */ }
}

export const ChatProvider = ({ children }) => {
  const { user, apps } = useAuth();
  const meId = user && typeof user === "object" ? user.user_id : null;
  const isInternal = user && user !== true && user !== false &&
    (user.role === "admin" || (apps || []).some((a) => !a.benefit_ets));

  const [unread, setUnread] = useState({ total: 0, alte_aperte: 0, per_conv: {} });
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("chatSound") !== "off");
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const soundRef = useRef(soundEnabled);
  const meRef = useRef(meId);
  useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { meRef.current = meId; }, [meId]);

  const toggleSound = useCallback(() => {
    setSoundEnabled((v) => {
      const nv = !v;
      localStorage.setItem("chatSound", nv ? "on" : "off");
      if (nv && "Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
      return nv;
    });
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/unread");
      setUnread(data);
    } catch { /* noop */ }
  }, []);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const notifyAlta = useCallback((m) => {
    if (!soundRef.current) return;
    if (!m || m.priority !== "alta") return;
    if (m.sender_id === meRef.current) return;
    playAlertSound();
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const n = new Notification("🔴 Messaggio urgente — Chat Assietica", {
          body: `${m.sender_name}: ${m.text}`.slice(0, 140),
          tag: m.id,
        });
        n.onclick = () => { window.focus(); window.location.href = `/chat?conv=${m.conv}`; };
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!isInternal) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      return;
    }
    if ("Notification" in window && Notification.permission === "default" && soundRef.current) {
      Notification.requestPermission().catch(() => {});
    }
    refreshUnread();
    const socket = io(BACKEND_URL, {
      path: "/api/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;
    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("chat_event", (evt) => {
      listenersRef.current.forEach((fn) => { try { fn(evt); } catch { /* noop */ } });
      if (evt.type === "message") notifyAlta(evt.message);
      refreshUnread();
    });
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [isInternal, refreshUnread, notifyAlta]);

  return (
    <ChatContext.Provider value={{ isInternal, unread, refreshUnread, subscribe, connected, soundEnabled, toggleSound }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext) || { isInternal: false, unread: { total: 0, alte_aperte: 0 }, refreshUnread: () => {}, subscribe: () => () => {}, connected: false, soundEnabled: true, toggleSound: () => {} };
